import { ConfigAttentionButton } from './ConfigAttentionButton'
import { attentionCount, type ConfigAttention } from '../../lib/configAttention'
import { useEffect, useState } from 'react'
import { SidebarFolder, SidebarItem, SidebarTree, VelticaMascot } from '@webbuilder/ui'
import { BrandLockup } from '../BrandLockup'
import { helpUrlForSection } from '../../lib/helpCenter'
import { useI18n } from '../../lib/i18n'
import { newItemsResource, useSectionNewItems } from '../../lib/sectionNewItems'
import { useSidebarFavorites } from '../../lib/sidebarFavorites'
import { DashboardUserMenu } from '../DashboardUserMenu'
import { SidebarProjectCard, type SidebarDeletedProject } from './SidebarProjectCard'
import type { UserRole } from '../../lib/api'
import { canOpenSection, type ProjectAccessRole } from '@webbuilder/shared-types'
import {
  IconCategory,
  IconAddressBook,
  IconBriefcase,
  IconApps,
  IconArrowUpRight,
  IconBlocks,
  IconBuildingBank,
  IconCalendarEvent,
  IconCalendarStats,
  IconCashRegister,
  IconFileTypePdf,
  IconChartBar,
  IconClipboardList,
  IconCoins,
  IconDatabase,
  IconEye,
  IconArticle,
  IconFileText,
  IconHelpCircle,
  IconHome,
  IconLayoutBoard,
  IconLock,
  IconMail,
  IconPhoto,
  IconReceipt,
  IconSchool,
  IconSettings,
  IconSpeakerphone,
  IconUsers,
  IconKey,
  IconCalendarMonth,
  IconWorld,
  IconTicket,
  IconPackage,
  IconTruckDelivery,
  IconX,
  IconStar,
} from '@tabler/icons-react'

/* Sin `plans`: la membresía es de la persona, no del sitio, así que dejó de ser
   una sección del proyecto y pasó al menú de usuario, junto a Facturación. Ver
   `WorkspaceGlobalView`. */
export type WorkspaceSection = 'overview' | 'contacts' | 'pages' | 'blog' | 'cms' | 'analytics' | 'forms' | 'emails' | 'bookings' | 'bookingsCalendar' | 'bookingsList' | 'emailMarketing' | 'courses' | 'payments' | 'digitalProducts' | 'physicalProducts' | 'services' | 'memberships' | 'orders' | 'coupons' | 'shipping' | 'payouts' | 'salesAnalytics' | 'bookingsAnalytics' | 'team' | 'config' | 'general' | 'brand' | 'business' | 'region' | 'policies' | 'publish' | 'usage' | 'backups' | 'transfer' | 'stripe' | 'googleAnalytics' | 'omnisend' | 'openTable' | 'calendars' | 'collections'

/**
 * Sections still gated to admins. Single source of truth — the sidebar badge,
 * the workspace render guard and the Home redirect all read this, so a section
 * can never be visible in one and blocked in another.
 *
 * `config` no está aquí a propósito: es un índice, y lo que esconde cada
 * entrada ya lo decide la entrada. `stripe` sí, porque sale de `payments`.
 *
 * `forms` tampoco: está abierto a todos. Sus rutas del worker no piden rol de
 * administrador —piden ser dueño del proyecto, que es la condición correcta—,
 * así que la reja sólo estaba en el menú, escondiendo tras un «Próximamente»
 * algo que ya funcionaba.
 *
 * `contacts` sale por lo mismo, y con más motivo: vive en el mismo router que
 * Formularios (`routes/forms.ts`) y sus cinco rutas ya se guardan con
 * `requireOwnedProject`, no con rol. Lo que sí lo acota es el plan —el tope de
 * `CONTACT_LIMITS`, cien fichas en el gratuito—, que es un límite comercial y se
 * aplica solo en el servidor.
 *
 * `bookings` también sale: todas sus rutas pasan por
 * `requireOwnedProjectWith`, sin pedir rol de administrador. La agenda pertenece
 * a quien posee el proyecto; la reja anterior sólo la ocultaba en el cliente.
 */
/* `backups` salió de aquí al dejar de ser una maqueta: ahora guarda copias de
   verdad en cada publicación y es una función del plan, no una vista de staff.

   Y con él salió el grupo de comercio entero —`payments`, `orders`, `payouts`,
   `salesAnalytics` y `stripe`—, por el mismo motivo que antes salieron
   Formularios y Contactos: sus rutas del worker nunca pidieron rol de
   administrador, sino ser dueño del proyecto, así que la reja vivía sólo en el
   menú. Cobrar y ver el dinero propio no es una función que se alquile.

   Lo que sí es de pago es COBRAR: conectar Stripe pide plan, y publicar una
   oferta pide cuenta conectada. Las dos se deciden en el servidor —ver
   `assertCanConnectStripe` y `assertCanPublish` en `routes/commerce.ts`—.
   Crear el catálogo no cuesta nada. Un tope comercial se aplica donde no se
   puede esquivar, no escondiendo el menú. */
/* `team` salió de aquí al dejar de ser una maqueta. Estaba en la lista de staff
   por lo mismo que estuvieron `backups` y el grupo de comercio: no existía de
   verdad, así que la reja del menú hacía de «no mirar todavía». Ahora es una
   sección del proyecto como las demás, y quién la ve lo decide el rol —en el
   menú y, lo que importa, en el servidor: ver `requireProjectAccess`—. */
export const ADMIN_ONLY_SECTIONS: WorkspaceSection[] = ['emails', 'emailMarketing', 'courses', 'omnisend']

/* `labelKey` y no `label`: estas listas se construyen al cargar el módulo, donde
   todavía no hay traductor ni idioma elegido. Guardan la clave y quien las
   pinta la resuelve, que además es lo que hace que el menú cambie de idioma sin
   recargar. */
type Item = { id: WorkspaceSection; labelKey: string; icon: React.ReactNode; soon?: boolean }
/* `projects` es el listado de empresas. Vive aquí y no como sección porque no
   cuelga de un proyecto: está por encima de todos ellos. */
export type WorkspaceGlobalView = 'settings' | 'billing' | 'domains' | 'plans' | 'usage' | 'siteTemplates' | 'templates' | 'images' | 'users' | 'authCodes' | 'aiUnmet' | 'adminEmails'
type GlobalItem = { id: WorkspaceGlobalView; labelKey: string; icon: React.ReactNode; adminOnly?: boolean }
type GroupId = 'website' | 'catalog' | 'sales' | 'analytics' | 'apps' | 'admin'
type MenuGroup = { id: GroupId; labelKey: string; icon: React.ReactNode; items: Item[]; globalItems?: GlobalItem[]; trailingItems?: Item[]; adminOnly?: boolean }

function navIcon(icon: React.ReactNode, tone: string) {
  return <span className={`dash-widget-glass-icon dash-sidebar-glass-icon is-${tone}`}>{icon}</span>
}

const WEBSITE: Item[] = [
  { id: 'pages', labelKey: 'nav.pages', icon: navIcon(<IconFileText size={15} />, 'blue') },
  /* Antes que Contenido: es lo que busca quien arranca un sitio, y el CMS —que
     es lo mismo por dentro— muchos sitios no lo necesitan. */
  { id: 'blog', labelKey: 'nav.blog', icon: navIcon(<IconArticle size={15} />, 'rose') },
  { id: 'cms', labelKey: 'nav.cms', icon: navIcon(<IconDatabase size={15} />, 'violet') },
  { id: 'forms', labelKey: 'nav.forms', icon: navIcon(<IconClipboardList size={15} />, 'amber') },
]
/**
 * Todo lo que ofreces, en una carpeta.
 *
 * Estaba repartido en tres sitios: «Todas las ofertas» dentro de Ventas,
 * Productos digitales y Servicios sueltos al primer nivel, y los servicios con
 * agenda en su propia carpeta con las citas. Cinco entradas para UNA pregunta
 * —«¿qué vendo?»— y ninguna al lado de la otra: para comparar un producto
 * digital con un servicio había que subir, bajar y abrir otra carpeta.
 *
 * Las cuatro primeras son la misma lista con distinto filtro; la última es lo
 * que la gente ha reservado contra ellas. Va aquí y no en Ventas por lo mismo
 * que estaba junto a los servicios con agenda: es su consecuencia directa, y en
 * un negocio de citas se abre más que ninguna.
 *
 * Lo que se quedó en Ventas es el dinero: lo cobrado y lo depositado, que ya no
 * es catálogo sino registro.
 */
const CATALOG: Item[] = [
  { id: 'payments', labelKey: 'nav.payments', icon: navIcon(<IconReceipt size={15} />, 'green') },
  { id: 'digitalProducts', labelKey: 'nav.digitalProducts', icon: navIcon(<IconFileTypePdf size={15} />, 'violet') },
  { id: 'physicalProducts', labelKey: 'nav.physicalProducts', icon: navIcon(<IconPackage size={15} />, 'violet') },
  { id: 'services', labelKey: 'nav.services', icon: navIcon(<IconBriefcase size={15} />, 'amber') },
  { id: 'bookings', labelKey: 'nav.bookings', icon: navIcon(<IconCalendarEvent size={15} />, 'teal') },
  { id: 'bookingsCalendar', labelKey: 'nav.bookingsCalendar', icon: navIcon(<IconCalendarMonth size={15} />, 'teal') },
  /* Membresías y paquetes de clases: lo que da acceso a las reservas. */
  { id: 'memberships', labelKey: 'nav.memberships', icon: navIcon(<IconStar size={15} />, 'amber') },
  { id: 'collections', labelKey: 'nav.collections', icon: navIcon(<IconCategory size={15} />, 'violet') },
]

/**
 * El dinero: lo que ya se cobró y cuándo llega al banco.
 *
 * Tenía delante el catálogo entero, que es donde se trabaja; ahora que aquél
 * tiene carpeta propia, esto es lo que queda — dos registros que se consultan,
 * no dos sitios donde se hace algo.
 */
const SALES: Item[] = [
  /* Caja registradora y no carrito: el carrito es el paso del comprador y
     arrastraba consigo la idea de un pedido que se prepara y se envía. Aquí no
     se envía nada — son pagos que Stripe ya confirmó. */
  { id: 'orders', labelKey: 'nav.orders', icon: navIcon(<IconCashRegister size={15} />, 'green') },
  /* Los descuentos, junto a lo cobrado: un cupón se decide mirando las ventas y
     se mide en ellas. */
  { id: 'coupons', labelKey: 'nav.coupons', icon: navIcon(<IconTicket size={15} />, 'green') },
  /* Cómo llegan los productos físicos: las tarifas y los países. */
  { id: 'shipping', labelKey: 'nav.shipping', icon: navIcon(<IconTruckDelivery size={15} />, 'green') },
  { id: 'payouts', labelKey: 'nav.payouts', icon: navIcon(<IconBuildingBank size={15} />, 'green') },
]
/**
 * Los números, en una sola carpeta.
 *
 * Había dos «Analíticas» —una al final de Sitio web y otra al final de Ventas—
 * con el mismo nombre y en sitios distintos: para comparar cuánta gente entra
 * con cuánto se vende había que abrir dos carpetas, y el nombre repetido hacía
 * dudar de cuál era cuál. Juntas se llaman por lo que miden, no por dónde
 * colgaban, y hay una por cada área de trabajo del menú: el sitio, el dinero y
 * la agenda.
 */
const ANALYTICS: Item[] = [
  { id: 'analytics', labelKey: 'nav.analytics', icon: navIcon(<IconEye size={15} />, 'blue') },
  { id: 'salesAnalytics', labelKey: 'nav.salesAnalytics', icon: navIcon(<IconCoins size={15} />, 'green') },
  /* El mismo orden que las carpetas de arriba —Ventas antes que Reservas— y el
     mismo color por área: cada entrada de aquí es el espejo numérico de una
     carpeta de trabajo, así que se reconoce por su tono antes que por su
     nombre. */
  { id: 'bookingsAnalytics', labelKey: 'nav.bookingsAnalytics', icon: navIcon(<IconCalendarStats size={15} />, 'teal') },
]
// Contactos ya no vive aquí: es una entrada de primer nivel, abajo.
const APPS: Item[] = [
  { id: 'emails', labelKey: 'nav.emails', icon: navIcon(<IconMail size={15} />, 'sky') },
  { id: 'emailMarketing', labelKey: 'nav.emailMarketing', icon: navIcon(<IconSpeakerphone size={15} />, 'rose') },
  { id: 'courses', labelKey: 'nav.courses', icon: navIcon(<IconSchool size={15} />, 'indigo') },
]
const ADMIN_LINKS: GlobalItem[] = [
  /* Llevar varias empresas es lo de agencia, que todavía no existe como
     producto: quien no la administra entra directo a su único proyecto y no
     tiene ninguna lista que recorrer. */
  { id: 'users', labelKey: 'nav.users', icon: navIcon(<IconUsers size={15} />, 'azure'), adminOnly: true },
  /* Las solicitudes de código de transferencia. Es trabajo manual con plazo
     prometido —24 horas hábiles— así que necesita un sitio donde se vea, no una
     carpeta de correo. Ver `AdminAuthCodes`. */
  { id: 'authCodes', labelKey: 'nav.authCodes', icon: navIcon(<IconKey size={15} />, 'amber'), adminOnly: true },
  /* Lo que los clientes piden a la IA y no tenemos: para decidir qué
     construir con datos. Ver `AdminAiUnmet`. */
  { id: 'aiUnmet', labelKey: 'nav.aiUnmet', icon: navIcon(<VelticaMascot outline size={17} />, 'violet'), adminOnly: true },
  /* Todos los correos que manda el worker, en los dos idiomas. Ver `AdminEmails`. */
  { id: 'adminEmails', labelKey: 'nav.adminEmails', icon: navIcon(<IconMail size={15} />, 'sky'), adminOnly: true },
  { id: 'siteTemplates', labelKey: 'nav.siteTemplates', icon: navIcon(<IconLayoutBoard size={15} />, 'lavender') },
  { id: 'templates', labelKey: 'nav.templates', icon: navIcon(<IconBlocks size={15} />, 'periwinkle') },
  { id: 'images', labelKey: 'nav.images', icon: navIcon(<IconPhoto size={15} />, 'fuchsia'), adminOnly: true },
]

/**
 * Configuración ya no es una carpeta.
 *
 * Desplegada sólo enseñaba cinco nombres, y para saber si el dominio estaba
 * puesto o Stripe conectado había que entrar en cada uno y volver. Ahora es una
 * entrada de primer nivel que abre su propio índice, donde cada opción puede
 * decir en qué estado está. Sus secciones siguen existiendo por separado, así
 * que un enlace directo a cualquiera de ellas sigue funcionando igual.
 */
const GROUPS: MenuGroup[] = [
  { id: 'website', labelKey: 'nav.group.website', icon: navIcon(<IconWorld size={15} />, 'ocean'), items: WEBSITE },
  { id: 'catalog', labelKey: 'nav.group.catalog', icon: navIcon(<IconReceipt size={15} />, 'teal'), items: CATALOG },
  { id: 'sales', labelKey: 'nav.group.sales', icon: navIcon(<IconCoins size={15} />, 'green'), items: SALES },
  /* Después de las tres áreas de trabajo: se mira cuando ya hay algo que medir. */
  { id: 'analytics', labelKey: 'nav.group.analytics', icon: navIcon(<IconChartBar size={15} />, 'blue'), items: ANALYTICS },
  // Estas áreas todavía no están listas. El id interno se conserva para no
  // romper el estado abierto del menú ni los enlaces de sus futuras secciones.
  { id: 'apps', labelKey: 'nav.group.apps', icon: navIcon(<IconApps size={15} />, 'coral'), items: APPS },
  { id: 'admin', labelKey: 'nav.group.admin', icon: navIcon(<IconLock size={15} />, 'red'), items: [], globalItems: ADMIN_LINKS, adminOnly: true },
]

const TOP_ITEMS: Item[] = [
  { id: 'contacts', labelKey: 'nav.contacts', icon: navIcon(<IconAddressBook size={15} />, 'pink') },
  { id: 'team', labelKey: 'nav.team', icon: navIcon(<IconUsers size={15} />, 'cyan') },
  { id: 'config', labelKey: 'nav.config', icon: navIcon(<IconSettings size={15} />, 'steel') },
]

/**
 * Las que se abren desde el índice de Configuración.
 *
 * Se exporta porque la barra de arriba también las necesita: son las únicas
 * secciones del panel con un padre de verdad —una pantalla a la que se vuelve—
 * y por eso son las únicas que el migajero escribe con dos escalones.
 */
export const CONFIG_SECTIONS: WorkspaceSection[] = ['config', 'publish', 'backups', 'usage', 'general', 'business', 'region', 'policies', 'stripe', 'googleAnalytics', 'omnisend', 'openTable', 'calendars']

function groupForSection(section: WorkspaceSection): GroupId | null {
  return GROUPS.find((group) => group.items.some((item) => item.id === section) || group.trailingItems?.some((item) => item.id === section))?.id ?? null
}

function favoriteMatches(key: string, section: WorkspaceSection, globalView: WorkspaceGlobalView | null | undefined): boolean {
  if (globalView) return key === `global:${globalView}`
  return key === `section:${section}` || (key === 'section:config' && CONFIG_SECTIONS.includes(section))
}

export function WorkspaceSidebar({
  active,
  configAttention,
  activeBookingCount,
  globalActive,
  onSelect,
  isAdmin,
  role = isAdmin ? 'admin' : 'user',
  onGlobalNavigate,
  avatarUrl,
  userName,
  userId,
  displayName,
  open,
  onClose,
  onHome,
  project,
  projectRole = 'owner',
}: {
  active: WorkspaceSection
  configAttention?: ConfigAttention
  activeBookingCount?: number | null
  globalActive?: WorkspaceGlobalView | null
  onSelect: (section: WorkspaceSection) => void
  isAdmin: boolean
  role?: UserRole
  /**
   * Qué eres en ESTE proyecto, que no es lo mismo que `role`.
   *
   * `role` es el rol en véltica —staff o no— y decide las pantallas internas.
   * Éste decide las del proyecto, y por defecto es `owner` para que un worker
   * antiguo, que no lo manda, no deje a nadie sin menú.
   */
  projectRole?: ProjectAccessRole
  onGlobalNavigate: (view: WorkspaceGlobalView) => void
  avatarUrl: string
  userName: string
  userId?: string | undefined
  displayName: string
  open?: boolean
  onClose?: () => void
  /** A la portada. Sólo se usa en la cabecera del cajón de teléfono —ver más
      abajo—: en escritorio la marca vive en la barra de arriba, que ahí sí se
      ve. */
  onHome?: () => void
  /** El proyecto actual y sus hermanos, para la tarjeta del pie. */
  project?: {
    currentProjectId: string
    currentProjectName: string
    /** Los favicons del sitio, que son la cara del proyecto. */
    faviconLight?: string | undefined
    faviconDark?: string | undefined
    loading?: boolean | undefined
    projects: Array<{ id: string; name: string; role?: ProjectAccessRole }>
    /** Los borrados que aún se pueden recuperar. */
    deletedProjects?: SidebarDeletedProject[]
    onSelectProject: (projectId: string) => void
    onOpenDeleted?: ((project: SidebarDeletedProject) => void) | undefined
    onCreateProject: () => void
  } | undefined
}) {
  /* El plan ya no se consulta aquí: lo único que lo usaba era la insignia
     «Upgrade» del renglón de Planes, y ese renglón se mudó al menú de usuario.
     La tarjeta de mejorar del pie pregunta por su cuenta. */

  const { t, language } = useI18n()
  const { favorites, toggleFavorite } = useSidebarFavorites(userId)
  const [favoriteSelection, setFavoriteSelection] = useState<string | null>(null)
  const activeFavorite = favoriteSelection && favorites.includes(favoriteSelection) && favoriteMatches(favoriteSelection, active, globalActive)
    ? favoriteSelection : null
  useEffect(() => {
    setFavoriteSelection((key) => key && favorites.includes(key) && favoriteMatches(key, active, globalActive) ? key : null)
  }, [active, globalActive, favorites])
  const allowedConfigAttention = configAttention ? {
    policies: canOpenSection(projectRole, 'policies') ? configAttention.policies : null,
    business: canOpenSection(projectRole, 'business') ? configAttention.business : null,
  } : undefined
  const configNeedsAttention = Boolean(allowedConfigAttention && attentionCount(allowedConfigAttention) > 0)
  const pinAction = (key: string, label: string) => userId ? (
    <button type="button" className={`dash-sidebar-pin${favorites.includes(key) ? ' is-pinned' : ''}`}
      aria-pressed={favorites.includes(key)} aria-label={t(favorites.includes(key) ? 'nav.unpin' : 'nav.pin', { name: label })}
      title={t(favorites.includes(key) ? 'nav.unpin' : 'nav.pin', { name: label })}
      onClick={() => toggleFavorite(key)}><IconStar size={14} aria-hidden="true" /></button>
  ) : undefined
  const newItems = useSectionNewItems()
  const hasNew = (section: string) => {
    const resource = newItemsResource(section)
    return resource ? newItems?.hasNew(resource) : false
  }
  const newDot = <span className="dash-sidebar-new-dot" role="img" aria-label={t('nav.newItems')} title={t('nav.newItems')} />

  const [openGroups, setOpenGroups] = useState<Set<GroupId>>(() => {
    const current = groupForSection(active)
    return current ? new Set([current]) : new Set()
  })

  useEffect(() => {
    if (activeFavorite) {
      setOpenGroups((groups) => groups.size === 0 ? groups : new Set())
      return
    }
    const current = globalActive
      ? GROUPS.find((group) => group.globalItems?.some((item) => item.id === globalActive))?.id ?? null
      : groupForSection(active)
    // Las entradas de primer nivel —Inicio, Analíticas, Reservas, Contactos, Planes,
    // Equipo, Configuración— cierran los grupos: si no, seleccionar una dejaba
    // abierto el grupo anterior y la selección parecía estar en dos sitios.
    if (!current) {
      setOpenGroups((groups) => (groups.size === 0 ? groups : new Set()))
      return
    }
    setOpenGroups((groups) => {
      if (groups.has(current) && groups.size === 1) return groups
      return new Set([current])
    })
  }, [active, globalActive, activeFavorite])

  const toggleGroup = (groupId: GroupId) => {
    setOpenGroups((groups) => {
      if (groups.has(groupId)) return new Set()
      return new Set([groupId])
    })
  }

  const renderItem = (item: Item, index: number, level: 1 | 2 = 2, favorite = false) => {
    const adminOnly = ADMIN_ONLY_SECTIONS.includes(item.id)
    /* Lo que el rol no abre no se pinta apagado: se quita.
       Una entrada deshabilitada dice «esto existe y no es para ti», que es
       ruido en el menú de alguien a quien invitaron a hacer una cosa concreta.
       «Próximamente» sí se queda gris, porque ahí el mensaje es otro: llegará. */
    if (!canOpenSection(projectRole, item.id)) return null
    if (item.soon || (!isAdmin && adminOnly)) {
      return (
        <SidebarItem
          key={`${item.id}-${index}`}
          icon={item.icon}
          label={t(item.labelKey)}
          badge={t('nav.soon')}
          disabled
          onSelect={() => undefined}
        />
      )
    }
    return (
      <SidebarItem
        key={`${item.id}-${index}`}
        icon={item.icon}
        label={t(item.labelKey)}
        level={level}
        {...(item.id === 'config' ? { className: `dash-config-nav-item${userId ? ' has-pin' : ''}${configNeedsAttention ? ' has-warning' : ''}`, actionsOpen: configNeedsAttention } : {})}
        actions={userId || (item.id === 'config' && configNeedsAttention) ? <>{pinAction(`section:${item.id}`, t(item.labelKey))}{item.id === 'config' && allowedConfigAttention && configNeedsAttention ? (
          <ConfigAttentionButton attention={allowedConfigAttention} onSelect={(section) => { setFavoriteSelection(favorite ? `section:${item.id}` : null); onSelect(section); onClose?.() }} />
        ) : null}</> : undefined}
        labelPrefix={hasNew(item.id) ? newDot : null}
        {...(item.id === 'bookingsCalendar' && activeBookingCount && activeBookingCount > 0 ? {
          className: 'dash-sidebar-count-badge',
          count: activeBookingCount.toLocaleString(language),
          title: t('nav.activeAppointments', { count: activeBookingCount }),
        } : {})}
        selected={!globalActive && (item.id === 'config' ? CONFIG_SECTIONS.includes(active) : active === item.id) && (favorite ? activeFavorite === `section:${item.id}` : !activeFavorite)}
        onSelect={() => { setFavoriteSelection(favorite ? `section:${item.id}` : null); onSelect(item.id); onClose?.() }}
      />
    )
  }

  /**
   * Si esta cuenta puede abrir una sección de este proyecto.
   *
   * Existe porque media docena de entradas de primer nivel —Reservas, Contactos,
   * Planes, Configuración— se pintan a mano y no pasan por `renderItem`, que es
   * donde vivía el filtro. Así que el rol recortaba las que están dentro de una
   * carpeta y dejaba intactas las de fuera: un invitado con rol de Contenido veía
   * Reservas y Planes en el menú y sólo se enteraba al pulsarlas.
   */
  const opens = (section: WorkspaceSection) => canOpenSection(projectRole, section)

  const renderGlobal = (item: GlobalItem, level: 1 | 2 = 2, favorite = false) => (
    <SidebarItem key={item.id} icon={item.icon} label={t(item.labelKey)} level={level}
      actions={pinAction(`global:${item.id}`, t(item.labelKey))} selected={globalActive === item.id && (favorite ? activeFavorite === `global:${item.id}` : !activeFavorite)}
      onSelect={() => { setFavoriteSelection(favorite ? `global:${item.id}` : null); onGlobalNavigate(item.id); onClose?.() }} />
  )

  const renderGroup = (group: MenuGroup) => {
    if (group.adminOnly && role !== 'admin' && role !== 'designer') return null
    /* Una carpeta cuyas entradas se ha llevado todas el rol no se pinta vacía:
       un rótulo «Ventas» que se despliega y no tiene nada dentro promete una
       sección que no existe para quien lo mira. Las globales no cuentan para el
       rol —no cuelgan del proyecto— pero sí para decidir si queda algo. */
    const visible = group.items.filter((item) => opens(item.id))
    const visibleTrailing = group.trailingItems?.filter((item) => opens(item.id)) ?? []
    const visibleGlobal = group.globalItems?.filter((item) => !item.adminOnly || role === 'admin') ?? []
    if (visible.length + visibleTrailing.length + visibleGlobal.length === 0) return null

    const expanded = openGroups.has(group.id)
    const collapsedCount = group.id === 'catalog' && !expanded && visible.some((item) => item.id === 'bookingsCalendar')
      ? activeBookingCount ?? 0
      : 0
    const containsActive = (!globalActive && (
      group.items.some((item) => item.id === active)
      || Boolean(group.trailingItems?.some((item) => item.id === active))
    ))
      || Boolean(globalActive && group.globalItems?.some((item) => item.id === globalActive))
    return (
      <SidebarFolder
        key={group.id}
        icon={group.icon}
        label={t(group.labelKey)}
        expanded={expanded}
        {...(collapsedCount > 0 ? {
          count: collapsedCount.toLocaleString(language),
          className: 'dash-sidebar-count-badge',
          title: t('nav.activeAppointments', { count: collapsedCount }),
        } : {})}
        labelPrefix={!expanded && [...visible, ...visibleTrailing].some((item) => hasNew(item.id)) ? newDot : null}
        containsSelected={!activeFavorite && containsActive}
        onToggle={() => toggleGroup(group.id)}
      >
        {group.items.map((item, index) => renderItem(item, index))}
        {visibleGlobal.map((item) => renderGlobal(item))}
        {group.trailingItems?.map((item, index) => renderItem(item, index))}
      </SidebarFolder>
    )
  }

  const pinnedRows = favorites.map((key, index) => {
    if (key.startsWith('section:')) {
      const item = [...TOP_ITEMS, ...GROUPS.flatMap((group) => [...group.items, ...group.trailingItems ?? []])].find((entry) => key === `section:${entry.id}`)
      return item && !item.soon && (isAdmin || !ADMIN_ONLY_SECTIONS.includes(item.id)) ? renderItem(item, index, 1, true) : null
    }
    const global = ADMIN_LINKS.find((entry) => key === `global:${entry.id}`)
    return global && (role === 'admin' || (role === 'designer' && !global.adminOnly)) ? renderGlobal(global, 1, true) : null
  }).filter((row) => row !== null)

  return (
    <aside className={`dash-sidebar${open ? ' is-open' : ''}`}>
      {/* Sólo se ve en el teléfono: el cajón pasa a cubrir la barra de arriba
          entera, así que se lleva consigo lo que vivía ahí —la marca y el
          hamburguesa que lo abre— y tiene que darle a las dos un sitio propio
          dentro. En escritorio el cajón no existe como tal y esta fila se
          queda oculta por CSS. */}
      <div className="dash-sidebar-mobile-head">
        {/* La marca entera y sin tarjeta, como en la barra de arriba. */}
        <button
          type="button"
          className="dash-onboarding-brand-lockup"
          aria-label={t('topbar.home')}
          onClick={() => { onHome?.(); onClose?.() }}
        >
          <BrandLockup mascotSize={24} />
        </button>
        <button
          type="button"
          className="dash-sidebar-close"
          aria-label={t('common.close')}
          onClick={() => onClose?.()}
        >
          <IconX size={16} aria-hidden="true" />
        </button>
      </div>
      <div className="dash-sidebar-scroll-content">
        <SidebarTree label={t('nav.aria')} className="dash-nav-tree">
          {pinnedRows.length ? <div className="dash-sidebar-favorites" role="group" aria-label={t('nav.favorites')}>
            <div className="dash-sidebar-favorites-title"><IconStar size={12} aria-hidden="true" />{t('nav.favorites')}</div>
            {pinnedRows}
            <div className="dash-sidebar-nav-rule" role="none" />
          </div> : null}
          <SidebarItem
            icon={navIcon(<IconHome size={15} />, 'home')}
            label={t('nav.home')}
            selected={!globalActive && active === 'overview'}
            onSelect={() => { setFavoriteSelection(null); onSelect('overview'); onClose?.() }}
          />
          {GROUPS[0] ? renderGroup(GROUPS[0]) : null}
          {/*
            El catálogo, justo debajo del sitio: lo que vendes es contenido del
            sitio, hermano de Páginas y Contenido, no una aplicación aparte a la
            que se entra. Dentro va todo lo que se ofrece —y las citas que sale
            de ello—; ver `CATALOG`.
          */}
          {GROUPS.filter((group) => group.id === 'catalog').map((group) => renderGroup(group))}
          {/* Y después el dinero, que se consulta cuando ya se vendió. */}
          {GROUPS.filter((group) => group.id === 'sales').map((group) => renderGroup(group))}
          {/* Cierra las áreas de trabajo: se mira cuando ya hay algo que medir,
              y mide las de arriba a la vez. */}
          {GROUPS.filter((group) => group.id === 'analytics').map((group) => renderGroup(group))}
          {/* Contactos es transversal: alimenta formularios, inbox y marketing por
              igual, así que meterlo dentro de una de esas apps lo escondía. */}
          {TOP_ITEMS.filter((item) => item.id === 'contacts').map((item, index) => renderItem(item, index, 1))}
          {/* Planes ya no está aquí.
              Lo que se contrata es la MEMBRESÍA de quien mira, la misma esté en
              el proyecto que esté, así que vive en el menú de usuario junto a
              Facturación. Como sección del proyecto sugería que cada sitio
              llevara su plan —y encima quedaba fuera del alcance de quien no
              tiene ninguno abierto—. Ver `WorkspaceGlobalView`. */}
          {/* Equipo sale de Configuración y se queda solo: no es un ajuste del
              sitio, es quién trabaja en él — la misma razón por la que
              Contactos tampoco vive dentro de una app. */}
          {/* La reja es el ROL, no `isAdmin`. Se quedó atada a éste de cuando
              Equipo era una maqueta de staff; al pasar a ser una sección de
              verdad y salir de `ADMIN_ONLY_SECTIONS`, esta rama seguía enseñando
              «Próximamente» a cualquier cliente que no fuera de la casa — o sea,
              a todos. */}
          {TOP_ITEMS.filter((item) => item.id === 'team').map((item, index) => renderItem(item, index, 1))}
          {/* Las áreas aún no disponibles van después de Equipo: primero queda
              todo lo que ya se puede usar y después lo que está por llegar. */}
          {GROUPS.filter((group) => group.id === 'apps').map((group) => renderGroup(group))}
          {/* El aviso de que ahí dentro falta algo por rellenar —las políticas a
              medias, los datos del negocio—. Va en el menú y no sólo dentro de
              Configuración porque quien no entra ahí es justamente quien no se
              entera: el sitio publica un aviso de privacidad con huecos y nada
              se lo dice. El aviso explica los pendientes y abre su ajuste. */}
          {TOP_ITEMS.filter((item) => item.id === 'config').map((item, index) => renderItem(item, index, 1))}
          {GROUPS.filter((group) => group.id === 'admin' && (!group.adminOnly || role === 'admin' || role === 'designer')).map((group) => renderGroup(group))}

          {/* Ayuda cierra la lista, detrás de una raya.
              Estaba abajo del todo, encima de la tarjeta del proyecto, y ahí
              partía en dos la esquina que dice con qué identidad y sobre qué se
              está trabajando — con un enlace que no es ni lo uno ni lo otro.
              Aquí es lo que es: el último renglón del menú, separado del resto
              porque no lleva a una sección del sitio sino fuera de él.

              Y ahora literalmente fuera: un ancla a `help.veltica.com`, no una
              vista del panel. Los artículos viven en un sitio abierto —sin
              sesión y indexable—, así que una copia dentro del panel sólo podía
              ir por detrás de aquél o repetirlo. Nunca va seleccionado: no es un
              sitio en el que se pueda estar. */}
          <div className="dash-sidebar-nav-rule" role="none" />
          <SidebarItem
            icon={navIcon(<IconHelpCircle size={15} />, 'cerulean')}
            label={t('nav.help')}
            href={helpUrlForSection(language, active, globalActive)}
            trailing={<IconArrowUpRight size={14} />}
            onSelect={() => onClose?.()}
          />
        </SidebarTree>
      </div>

      <div className="dash-sidebar-bottom-actions">
        {/* El proyecto encima de la cuenta: las dos cosas que dicen «con qué
            identidad y sobre qué estoy trabajando», juntas en la misma esquina.
            Sólo cuando hay lista que ofrecer — sin ella la tarjeta sería un
            botón que no lleva a ninguna parte. */}
        {project ? (
          <SidebarProjectCard
            projects={project.projects}
            deletedProjects={project.deletedProjects ?? []}
            {...(project.onOpenDeleted ? { onOpenDeleted: project.onOpenDeleted } : {})}
            currentProjectId={project.currentProjectId}
            currentProjectName={project.currentProjectName}
            {...(project.faviconLight ? { faviconLight: project.faviconLight } : {})}
            {...(project.faviconDark ? { faviconDark: project.faviconDark } : {})}
            loading={project.loading ?? false}
            onSelectProject={(id) => { project.onSelectProject(id); onClose?.() }}
            onCreateProject={() => { project.onCreateProject(); onClose?.() }}
          />
        ) : null}
        <div className="dash-sidebar-account">
          <DashboardUserMenu
            projectRole={projectRole}
            avatarUrl={avatarUrl}
            userName={userName}
            displayName={displayName}
            onOpenSettings={() => { onGlobalNavigate('settings'); onClose?.() }}
            onOpenPlans={() => { onGlobalNavigate('plans'); onClose?.() }}
            onOpenUsage={() => { onGlobalNavigate('usage'); onClose?.() }}
            onOpenBilling={() => { onGlobalNavigate('billing'); onClose?.() }}
            onOpenDomains={() => { onGlobalNavigate('domains'); onClose?.() }}
          />
        </div>
      </div>
    </aside>
  )
}
